/// <amd-module name="Artifacts/Package/components/package-details-common/PackageStats" />
import { IMetricProps, Metrics } from "Artifacts/Package/components/package-details-common/Metrics";
import { PackageAttribute } from "Artifacts/Package/components/package-details-common/PackageAttribute";
import { IPackageDetailsModuleState } from "Artifacts/Package/redux";
import * as PackageRes from "Artifacts/Package/Resources";
import { getFeedNameWithNewView, isPublicFeed } from "Artifacts/Root/Helpers/FeedHelpers";
import {
  getPackageMetricsAsync,
  getPackageVersionMetricsAsync,
} from "Artifacts/Root/helpers/PackageMetricsHelpers";
import { IFeed, IPackage } from "Artifacts/Root/Types";
import { SkeletalPackageVersion } from "Artifacts/Root/types/AzureArtifacts";
import { MetricType } from "Artifacts/Root/types/MetricType";
import { PackageMetrics, PackageVersionMetrics } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { connect } from "react-redux";
import { IVssPageContext } from "VSS/Platform/Context";

export interface IPackageStatsProps {
  pageContext: IVssPageContext;
  feed: IFeed;
  package: IPackage;
  versionId: string;
  isMetricsEnabled: boolean;
}

export interface IPackageStatsState {
  packageMetrics?: PackageMetrics;
  packageVersionMetrics?: PackageVersionMetrics;
  isFeedPublic: boolean;
  versionDownloadProps?: IMetricProps;
  totalDownloadProps?: IMetricProps;
  versionUserProps?: IMetricProps;
  totalUserProps?: IMetricProps;
}

interface IPackageStatsMapStateToProps {
  versions?: SkeletalPackageVersion[];
}

const mapStateToProps = (state: IPackageDetailsModuleState): IPackageStatsMapStateToProps => {
  const pkg = state.packageState.data.pkg;
  if (pkg == null) {
    throw new Error(
      "Package stats did not receive package object from redux store. Ensure component is not rendered before loading finished.",
    );
  }

  return {
    versions: state.packageState.data.packageVersionsList,
  };
};

class PackageStats extends React.Component<
  IPackageStatsProps & IPackageStatsMapStateToProps,
  IPackageStatsState
> {
  private _mounted: boolean;

  constructor(props: IPackageStatsProps) {
    super(props);
    this.state = {
      isFeedPublic: true,
      packageMetrics: undefined,
      packageVersionMetrics: undefined,
      totalDownloadProps: {},
      versionDownloadProps: {},
      totalUserProps: {},
      versionUserProps: {},
    };
    this._mounted = false;
  }

  public componentDidMount(): void {
    this._mounted = true;
    this.initialize();
  }

  public componentWillUnmount(): void {
    this._mounted = false;
  }

  public render(): JSX.Element {
    return (
      <div className="package-stats">
        <PackageAttribute title={PackageRes.PackageAttributeTitle_Stats}>
          <div>
            <Metrics {...this.state.versionDownloadProps} />
            <Metrics {...this.state.totalDownloadProps} />
            {!this.state.isFeedPublic && <Metrics {...this.state.versionUserProps} />}
            {!this.state.isFeedPublic && <Metrics {...this.state.totalUserProps} />}
          </div>
        </PackageAttribute>
      </div>
    );
  }

  private async initialize(): Promise<void> {
    if (!this.props.isMetricsEnabled) {
      return;
    }

    const feedId = getFeedNameWithNewView(this.props.feed.id, this.props.feed.selectedViewId);
    let packageMetrics: PackageMetrics | undefined;
    let packageVersionMetricsMap: { [key: string]: PackageVersionMetrics } | undefined;

    try {
      const projectId = this.props.feed.project ? this.props.feed.project.id : undefined;
      const packageIdMetrics: { [key: string]: PackageMetrics } = await getPackageMetricsAsync(
        this.props.pageContext,
        feedId,
        [this.props.package.id],
        projectId,
      );

      packageMetrics = packageIdMetrics ? packageIdMetrics[this.props.package.id] : undefined;

      packageVersionMetricsMap = await getPackageVersionMetricsAsync(
        this.props.pageContext,
        feedId,
        this.props.package.id,
        this.props.versions ? this.props.versions.map((v) => v.id) : [],
        projectId,
      );
    } catch (err) {
      if (err != null && err.status === 404) {
        // 404s fetching provenance or links are expected
      } else {
        throw err;
      }
    } finally {
      const isFeedPublic = isPublicFeed(this.props.feed);
      const packageVersionMetrics = packageVersionMetricsMap
        ? packageVersionMetricsMap[this.props.versionId]
        : undefined;
      const versionDownloadProps = {
        metrics: packageVersionMetrics,
        metricType: MetricType.DownloadCount,
        className: "version-downloads",
        resourceStrings: {
          singular: PackageRes.MetricsVersionDownload,
          plural: PackageRes.MetricsVersionDownloads,
        },
      };

      const totalDownloadProps = {
        metrics: packageMetrics,
        metricType: MetricType.DownloadCount,
        className: "total-downloads",
        resourceStrings: {
          singular: PackageRes.MetricsTotalDownload,
          plural: PackageRes.MetricsTotalDownloads,
        },
      };

      if (!isFeedPublic) {
        const versionUserProps = {
          metrics: packageVersionMetrics,
          metricType: MetricType.UniqueUsers,
          className: "version-users",
          resourceStrings: {
            singular: PackageRes.MetricsVersionUser,
            plural: PackageRes.MetricsVersionUsers,
          },
        };

        const totalUserProps = {
          metrics: packageMetrics,
          metricType: MetricType.UniqueUsers,
          className: "total-users",
          resourceStrings: {
            singular: PackageRes.MetricsTotalUser,
            plural: PackageRes.MetricsTotalUsers,
          },
        };
        if (this._mounted) {
          this.setState({
            packageMetrics,
            packageVersionMetrics,
            isFeedPublic,
            versionDownloadProps,
            totalDownloadProps,
            versionUserProps,
            totalUserProps,
          });
        }
      }

      if (this._mounted) {
        this.setState({
          packageMetrics,
          packageVersionMetrics,
          isFeedPublic,
          versionDownloadProps,
          totalDownloadProps,
        });
      }
    }
  }
}

const ConnectedPackageStatsContainer = connect<IPackageStatsMapStateToProps, unknown>(
  mapStateToProps,
  {},
)(PackageStats);

export { ConnectedPackageStatsContainer as PackageStats };
